import { useState } from 'react'
import { Box, Switch } from '@mui/material'
import QuestionIcon from '@mui/icons-material/HelpOutline'
import LocalAtmOutlinedIcon from '@mui/icons-material/LocalAtmOutlined'
import { DashboardCard, KpiCard, StatRow, SectionTitle } from '../../../../../ui'
import ChartSemiPie from '../CharComponent/ChartSemiPie'

const FlipCard = ({
  title,
  backTitle,
  frontLabel = 'Ingresos',
  frontValue = 0,
  backLabel = 'Gastos',
  backValue = 0,
  contentPtClass = 'pt-6 sm:pt-8 md:pt-12',
  numberUi
}) => {
  const {
    shouldCompactValues,
    formatCLPDisplay,
    formatCLPFull,
    parseDisplayNumber,
    kpiNumberClass
  } = numberUi
  const [flipped, setFlipped] = useState(false)
  const rotate = flipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
  const faceCommon = { backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }
  const toggle = () => setFlipped(v => !v)
  const frontIsCompact = shouldCompactValues && Math.abs(parseDisplayNumber(frontValue)) >= 1_000_000
  const backIsCompact = shouldCompactValues && Math.abs(parseDisplayNumber(backValue)) >= 1_000_000

  return (
    <div
      className='relative rounded-card bg-surface border border-border shadow-card h-auto min-h-[120px] sm:h-[135px] sm:min-h-[135px] md:h-[150px] md:min-h-[150px] cursor-pointer select-none'
      style={{ perspective: 1000 }}
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
      onClick={toggle}
      role='button'
      tabIndex={0}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && toggle()}
    >
      <div
        className='absolute inset-0 w-full h-full transition-transform duration-500'
        style={{ transformStyle: 'preserve-3d', transform: rotate, willChange: 'transform' }}
      >
        <div
          className={`absolute inset-0 flex flex-col items-center justify-center gap-1 ${contentPtClass}`}
          style={faceCommon}
        >
          <div className='absolute top-2 left-1/2 -translate-x-1/2 w-full px-2 text-center text-sm sm:text-base md:text-lg font-semibold text-text'>
            {title}
          </div>
          <div
            className={`w-full px-2 text-center text-[clamp(1.6rem,4.5vw,2.2rem)] font-bold text-success ${kpiNumberClass}`}
            title={frontIsCompact ? formatCLPFull(frontValue) : undefined}
          >
            {formatCLPDisplay(frontValue)}
          </div>
          <div className='text-label sm:text-sm font-medium text-muted'>{frontLabel}</div>
        </div>

        <div
          className={`absolute inset-0 flex flex-col items-center justify-center gap-1 ${contentPtClass}`}
          style={{ ...faceCommon, transform: 'rotateY(180deg)' }}
        >
          <div className='absolute top-2 left-1/2 -translate-x-1/2 w-full px-2 text-center text-sm sm:text-base md:text-lg font-semibold text-text'>
            {backTitle ?? title}
          </div>
          <div
            className={`w-full px-2 text-center text-[clamp(1.6rem,4.5vw,2.2rem)] font-bold text-danger ${kpiNumberClass}`}
            title={backIsCompact ? formatCLPFull(backValue) : undefined}
          >
            {formatCLPDisplay(backValue)}
          </div>
          <div className='text-label sm:text-sm font-medium text-muted'>{backLabel}</div>
        </div>
      </div>
    </div>
  )
}

export const TopCobranzasMiniCards = ({
  isSidebarConstrained,
  erIngresosAnual,
  erGastosAnual,
  erIngresosUlt3,
  erGastosUlt3,
  erIngresosMesActual,
  erGastosMesActual,
  numberUi
}) => (
  <div
    className={`grid grid-cols-1 sm:grid-cols-2 ${
      isSidebarConstrained ? 'xl:grid-cols-3' : 'lg:grid-cols-3'
    } gap-3 md:gap-2`}
  >
    <FlipCard
      title='Ingresos'
      frontLabel='Anual Neto'
      frontValue={erIngresosAnual}
      backTitle='Gastos'
      backLabel='Anual Neto'
      backValue={erGastosAnual}
      contentPtClass='pt-7 sm:pt-9 md:pt-8'
      numberUi={numberUi}
    />
    <FlipCard
      title='Ingresos'
      frontLabel='Últimos 3 meses Neto'
      frontValue={erIngresosUlt3}
      backTitle='Gastos'
      backLabel='Últimos 3 meses Neto'
      backValue={erGastosUlt3}
      contentPtClass='pt-7 sm:pt-9 md:pt-8'
      numberUi={numberUi}
    />
    <FlipCard
      title='Ingresos'
      frontLabel='Mes Actual Neto'
      frontValue={erIngresosMesActual}
      backTitle='Gastos'
      backLabel='Mes Actual Neto'
      backValue={erGastosMesActual}
      contentPtClass='pt-7 sm:pt-9 md:pt-8'
      numberUi={numberUi}
    />
  </div>
)

export const CardInfo = ({ title, amount, isMain, customColor, align = 'right', numberUi }) => {
  const {
    shouldCompactValues,
    isSidebarConstrained,
    isCompact,
    formatCLPDisplay,
    formatCLPFull,
    parseDisplayNumber,
    kpiNumberClass
  } = numberUi
  const numericAmount = parseDisplayNumber(amount)
  const isCompactValue = shouldCompactValues && Math.abs(numericAmount) >= 1_000_000
  const isNegative = numericAmount < 0
  const tone = customColor
    ? undefined
    : isNegative
    ? 'danger'
    : isMain
    ? 'primary'
    : 'info'

  const baseValueClass = isMain
    ? 'text-[clamp(1.5rem,3.2vw,2.25rem)]'
    : 'text-[clamp(1.1rem,2.4vw,1.65rem)]'
  const compactValueClass = isMain
    ? 'text-[clamp(1.2rem,4.6vw,1.9rem)]'
    : 'text-[clamp(1rem,3.4vw,1.4rem)]'
  const sidebarValueClass = isMain
    ? 'text-[clamp(1.25rem,2.6vw,1.8rem)]'
    : 'text-[clamp(1rem,2.1vw,1.45rem)]'
  const valueClassName = `${customColor || ''} ${kpiNumberClass} ${
    isSidebarConstrained ? sidebarValueClass : isCompact ? compactValueClass : baseValueClass
  } leading-tight`.trim()
  const labelClassName = isCompact ? 'text-[clamp(0.65rem,2.4vw,0.8rem)]' : ''

  return (
    <StatRow
      label={title}
      value={
        <span
          className={kpiNumberClass}
          title={isCompactValue ? formatCLPFull(amount) : undefined}
        >
          {formatCLPDisplay(amount)}
        </span>
      }
      size={isMain ? 'primary' : 'secondary'}
      align={align}
      tone={tone}
      valueClassName={valueClassName}
      labelClassName={labelClassName}
      className={isMain ? 'w-full min-w-0' : 'mt-2 w-full min-w-0'}
    />
  )
}

const CardInfoPie = ({ title, amount, isMain, customColor, numberUi }) => {
  const {
    shouldCompactValues,
    isSidebarConstrained,
    isCompact,
    isNarrow,
    formatCLPDisplay,
    formatCLPFull,
    parseDisplayNumber,
    kpiNumberClass
  } = numberUi
  const numericAmount = parseDisplayNumber(amount)
  const isCompactValue = shouldCompactValues && Math.abs(numericAmount) >= 1_000_000
  const isNegative = numericAmount < 0
  const tone = customColor ? 'default' : isNegative ? 'danger' : isMain ? 'primary' : 'info'
  const baseValueClass = isMain
    ? 'text-[clamp(1rem,2.2vw,1.4rem)]'
    : 'text-[clamp(0.95rem,2vw,1.3rem)]'
  const compactValueClass = isMain
    ? 'text-[clamp(0.9rem,3vw,1.2rem)]'
    : 'text-[clamp(0.85rem,2.8vw,1.1rem)]'
  const sidebarValueClass = isMain
    ? 'text-[clamp(0.9rem,2vw,1.25rem)]'
    : 'text-[clamp(0.85rem,1.8vw,1.1rem)]'
  const valueClassName = `${customColor || ''} ${kpiNumberClass} ${
    isSidebarConstrained ? sidebarValueClass : isCompact ? compactValueClass : baseValueClass
  } leading-tight tracking-tight`.trim()
  const labelClassName = isCompact ? 'text-[clamp(0.65rem,2.4vw,0.8rem)]' : ''

  return (
    <StatRow
      label={title}
      value={
        <span
          className={kpiNumberClass}
          title={isCompactValue ? formatCLPFull(amount) : undefined}
        >
          {formatCLPDisplay(amount)}
        </span>
      }
      size={isMain ? 'secondary' : 'tertiary'}
      align={isNarrow ? 'center' : 'right'}
      tone={tone}
      valueClassName={valueClassName}
      labelClassName={labelClassName}
      className={isMain ? 'w-full min-w-0' : 'mt-2 w-full min-w-0'}
    />
  )
}

export const BarChartKPIs = ({
  selectedMonthData,
  cardPaddingClass,
  isMobile,
  erMonthLabel,
  numberUi
}) => {
  if (!selectedMonthData) return null

  return (
    <DashboardCard className={`flex flex-col justify-center h-full ${cardPaddingClass} ${isMobile ? 'items-center text-center' : ''}`}>
      <CardInfo
        title='Resultado Operativo Período'
        amount={selectedMonthData.totalOriginal}
        isMain
        align={isMobile ? 'center' : 'right'}
        numberUi={numberUi}
      />
      <div className='mt-4 mb-10 sm:mb-16 md:mb-32'>
        <CardInfo
          title='Ingreso Mes'
          amount={selectedMonthData.ingresosOriginal}
          customColor='text-success'
          align={isMobile ? 'center' : 'right'}
          numberUi={numberUi}
        />
        <CardInfo
          title='Gasto Mes'
          amount={selectedMonthData.gastosOriginal}
          customColor='text-danger'
          align={isMobile ? 'center' : 'right'}
          numberUi={numberUi}
        />
      </div>
      <div className='text-center mt-6 sm:mt-8'>
        <h1 className='text-base sm:text-2xl font-bold border-2 border-border text-text rounded-full px-6 sm:px-10'>
          {erMonthLabel}
        </h1>
      </div>
    </DashboardCard>
  )
}

export const SemiPieCard = ({ data, isNarrow, smallCardBase, numberUi }) => {
  const sourceData = data?.data ?? data ?? {}
  const cobrado = Number(sourceData.cobrado ?? sourceData.pagado ?? 0)
  const porCobrar = Number(sourceData.porCobrar ?? sourceData.saldo ?? 0)
  const notasDeCredito = Number(sourceData.totalNC ?? 0)
  const summaryData = { data: { cobrado, porCobrar } }
  const layoutClass = isNarrow ? 'flex-col items-center' : 'flex-col xl:flex-row xl:items-center'
  const labelsWrapperClass = isNarrow
    ? 'w-full grid grid-cols-1 gap-2 min-w-0'
    : 'w-full xl:max-w-[40%] space-y-2 min-w-0'

  return (
    <DashboardCard className={`flex w-full min-w-0 ${layoutClass} gap-4 justify-center p-4 sm:p-6 ${smallCardBase}`}>
      <div className='w-full flex justify-center items-center min-w-0'>
        <ChartSemiPie summaryData={summaryData} />
      </div>
      <div className={labelsWrapperClass}>
        <CardInfoPie title='Por cobrar' amount={porCobrar} customColor='text-danger' isMain numberUi={numberUi} />
        <CardInfoPie title='Cobrado' amount={cobrado} customColor='text-success' numberUi={numberUi} />
        {cobrado > 0 && <CardInfoPie title='Notas de crédito' amount={notasDeCredito} customColor='text-info' numberUi={numberUi} />}
      </div>
    </DashboardCard>
  )
}

const getInitialBalanceMeta = ({
  hasInitialBalances = false,
  isLoading = false
}) => {
  const buttonToneClass = isLoading
    ? 'border-border bg-surface text-muted'
    : hasInitialBalances
    ? 'border-success/25 bg-success/10 text-success'
    : 'border-danger/25 bg-danger/10 text-danger'

  return { buttonToneClass }
}

const InitialBalanceAction = ({
  hasInitialBalances = false,
  isLoading = false,
  onOpenInitialBalanceModal,
  actionLabel = 'Agregar o editar saldo inicial',
  CustomTooltip
}) => {
  const { buttonToneClass } = getInitialBalanceMeta({
    hasInitialBalances,
    isLoading
  })
  const trigger = (
    <button
      type='button'
      onClick={onOpenInitialBalanceModal}
      aria-label={actionLabel}
      className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border shadow-card transition hover:-translate-y-[1px] hover:bg-border/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 ${buttonToneClass}`}
    >
      <LocalAtmOutlinedIcon fontSize='small' className='text-[1.15rem]' />
    </button>
  )

  if (!CustomTooltip) return trigger

  return (
    <CustomTooltip title={actionLabel} arrow>
      {trigger}
    </CustomTooltip>
  )
}

const PrimaryCashFlowMetric = ({
  amount,
  align = 'right',
  numberUi,
  hasInitialBalances = false,
  initialBalancesLoading = false,
  onOpenInitialBalanceModal,
  actionLabel = 'Agregar o editar saldo inicial',
  CustomTooltip
}) => {
  const {
    shouldCompactValues,
    isSidebarConstrained,
    isCompact,
    formatCLPDisplay,
    formatCLPFull,
    parseDisplayNumber,
    kpiNumberClass
  } = numberUi
  const numericAmount = parseDisplayNumber(amount)
  const isCompactValue = shouldCompactValues && Math.abs(numericAmount) >= 1_000_000
  const valueToneClass = numericAmount < 0 ? 'text-danger' : 'text-primary'
  const baseValueClass = 'text-[clamp(1.5rem,3.2vw,2.25rem)]'
  const compactValueClass = 'text-[clamp(1.2rem,4.6vw,1.9rem)]'
  const sidebarValueClass = 'text-[clamp(1.25rem,2.6vw,1.8rem)]'
  const valueClassName = `${valueToneClass} ${kpiNumberClass} ${
    isSidebarConstrained ? sidebarValueClass : isCompact ? compactValueClass : baseValueClass
  } w-full min-w-0 font-semibold leading-tight tabular-nums`.trim()
  const justifyClass = align === 'left' ? 'justify-start' : 'justify-end'
  const textAlignClass = align === 'left' ? 'text-left' : 'text-right'

  return (
    <div className='w-full min-w-0'>
      <div className={`flex w-full min-w-0 items-start gap-3 ${justifyClass}`}>
        <div className={`min-w-0 flex-1 ${textAlignClass}`}>
          <div
            className={valueClassName}
            title={isCompactValue ? formatCLPFull(amount) : undefined}
          >
            {formatCLPDisplay(amount)}
          </div>
        </div>
        <InitialBalanceAction
          hasInitialBalances={hasInitialBalances}
          isLoading={initialBalancesLoading}
          onOpenInitialBalanceModal={onOpenInitialBalanceModal}
          actionLabel={actionLabel}
          CustomTooltip={CustomTooltip}
        />
      </div>
      <div className={`mt-1 flex w-full min-w-0 flex-wrap items-center ${justifyClass}`}>
      </div>
    </div>
  )
}

const SummaryMetricRow = ({
  title,
  amount,
  tooltip,
  tooltipIconClass,
  CustomTooltip,
  numberUi,
  align = 'right'
}) => {
  const justifyClass = align === 'left' ? 'justify-start' : 'justify-end'

  return (
    <div className={`flex w-full items-start gap-1 ${justifyClass}`}>
      <CardInfo title={title} amount={amount} numberUi={numberUi} align={align} />
      <CustomTooltip title={tooltip}>
        <QuestionIcon fontSize="small" className={`${tooltipIconClass} mt-1 shrink-0`} />
      </CustomTooltip>
    </div>
  )
}

export const CashFlowSummaryPanel = ({
  operationalCashFlowAmount,
  promedioMensual,
  runAway,
  tooltipIconClass,
  CustomTooltip,
  numberUi,
  hasInitialBalances = false,
  initialBalancesLoading = false,
  onOpenInitialBalanceModal,
  align = 'right',
  actionLabel = 'Agregar o editar saldo inicial',
  className = ''
}) => {
  const alignmentClass = align === 'left' ? 'items-start text-left' : 'items-end text-right'

  return (
    <div className={`flex w-full min-w-0 flex-col gap-3 ${alignmentClass} ${className}`.trim()}>
      <PrimaryCashFlowMetric
        amount={operationalCashFlowAmount}
        align={align}
        numberUi={numberUi}
        hasInitialBalances={hasInitialBalances}
        initialBalancesLoading={initialBalancesLoading}
        onOpenInitialBalanceModal={onOpenInitialBalanceModal}
        actionLabel={actionLabel}
        CustomTooltip={CustomTooltip}
      />
      <div className='w-full space-y-1.5'>
        <SummaryMetricRow
          title='Burn Rate'
          amount={promedioMensual}
          tooltip='Es la velocidad a la que una startup o empresa consume su efectivo disponible, normalmente medida en gasto mensual.'
          tooltipIconClass={tooltipIconClass}
          CustomTooltip={CustomTooltip}
          numberUi={numberUi}
          align={align}
        />
        <SummaryMetricRow
          title='Runway'
          amount={runAway}
          tooltip='Es el tiempo que le queda a la empresa antes de quedarse sin dinero, calculado dividiendo el efectivo disponible por el burn rate.'
          tooltipIconClass={tooltipIconClass}
          CustomTooltip={CustomTooltip}
          numberUi={numberUi}
          align={align}
        />
      </div>
    </div>
  )
}

export const SimpleChartCard = ({
  data,
  onSelectMonth,
  selectedMonth,
  title = 'Cash In vs Cash Out',
  bankTimeframe,
  onChangeTimeframe,
  isCompact,
  isMobile,
  chartScrollSx,
  chartInnerSx,
  totales,
  operationalCashFlow,
  promedioMensual,
  runAway,
  tooltipIconClass,
  CustomTooltip,
  getMonthLabel,
  SimpleBarChart,
  numberUi,
  onOpenInitialBalanceModal,
  initialBalanceRows = [],
  initialBalancesLoading = false
}) => {
  const periodLabel = getMonthLabel(selectedMonth) || '—'
  const operationalCashFlowAmount = operationalCashFlow ?? totales?.diferencia
  const hasInitialBalances = Array.isArray(initialBalanceRows) && initialBalanceRows.length > 0

  return (
    <DashboardCard className={`relative flex w-full min-w-0 ${isCompact ? 'flex-col gap-4' : 'justify-between'} items-start`}>
      {isCompact ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className='flex flex-wrap items-center gap-2'>
              <div className="flex items-center gap-2 border border-border rounded-full px-2 py-1">
                <span className="text-xs sm:text-sm text-muted">Semana</span>
                <Switch
                  checked={bankTimeframe === 'month'}
                  onChange={(e) =>
                    onChangeTimeframe(null, e.target.checked ? 'month' : 'week')
                  }
                  color="primary"
                  size={isMobile ? 'medium' : 'small'}
                />
                <span className="text-xs sm:text-sm text-muted">Mes</span>
              </div>
            </div>
            <SectionTitle className="w-full sm:w-auto text-right leading-tight text-sm sm:text-base md:text-base">
              {title}
            </SectionTitle>
          </div>

          <Box sx={chartScrollSx}>
            <Box sx={chartInnerSx(480, 240)}>
              <SimpleBarChart
                data={data}
                onSelectMonth={onSelectMonth}
                selectedMonth={selectedMonth}
              />
            </Box>
          </Box>

          <div className='w-full flex flex-col gap-3'>
            <CashFlowSummaryPanel
              operationalCashFlowAmount={operationalCashFlowAmount}
              promedioMensual={promedioMensual}
              runAway={runAway}
              tooltipIconClass={tooltipIconClass}
              CustomTooltip={CustomTooltip}
              numberUi={numberUi}
              hasInitialBalances={hasInitialBalances}
              initialBalancesLoading={initialBalancesLoading}
              onOpenInitialBalanceModal={onOpenInitialBalanceModal}
            />
            <div className='mt-2 flex justify-center w-full'>
              <h1 className='text-sm font-semibold border border-border text-text rounded-full px-6 py-1'>
                {periodLabel}
              </h1>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className='absolute top-4 left-4 flex items-center gap-2'>
            <div className="flex items-center gap-2 border border-border rounded-full px-2 py-1">
              <span className="text-sm text-muted">Semana</span>
              <Switch
                checked={bankTimeframe === 'month'}
                onChange={(e) =>
                  onChangeTimeframe(null, e.target.checked ? 'month' : 'week')
                }
                color="primary"
                size="small"
              />
              <span className="text-sm text-muted">Mes</span>
            </div>
          </div>

          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-center px-4">
            <SectionTitle className="text-center">{title}</SectionTitle>
          </div>

          <div className="flex-1 flex justify-center items-center pt-10 min-h-[260px] min-w-0">
            <Box sx={chartScrollSx}>
              <Box sx={chartInnerSx(520, 260)}>
                <SimpleBarChart
                  data={data}
                  onSelectMonth={onSelectMonth}
                  selectedMonth={selectedMonth}
                />
              </Box>
            </Box>
          </div>

          <div className='text-right ml-6 flex self-stretch min-h-[260px] flex-col justify-between items-end'>
            <CashFlowSummaryPanel
              operationalCashFlowAmount={operationalCashFlowAmount}
              promedioMensual={promedioMensual}
              runAway={runAway}
              tooltipIconClass={tooltipIconClass}
              CustomTooltip={CustomTooltip}
              numberUi={numberUi}
              hasInitialBalances={hasInitialBalances}
              initialBalancesLoading={initialBalancesLoading}
              onOpenInitialBalanceModal={onOpenInitialBalanceModal}
              className='max-w-[320px]'
            />
            <div className='flex justify-center w-full pt-6'>
              <h1 className='text-base font-semibold border border-border text-text rounded-full px-12'>
                {periodLabel}
              </h1>
            </div>
          </div>
        </>
      )}
    </DashboardCard>
  )
}

export const AnnualAndMarginCard = ({
  shouldCompactValues,
  parseDisplayNumber,
  formatCLPDisplay,
  formatCLPFull,
  totalSaldoAcumulado,
  porcentajeValor,
  porcentajeLabel,
  kpiNumberClass
}) => (
  <DashboardCard className='flex flex-col justify-center items-center px-4 sm:px-6 py-6 sm:py-8 h-full'>
    <div className='flex flex-col items-center gap-1'>
      <KpiCard
        wrapper={false}
        align='center'
        value={
          <span
            className={kpiNumberClass}
            title={
              shouldCompactValues && Math.abs(parseDisplayNumber(totalSaldoAcumulado)) >= 1_000_000
                ? `${totalSaldoAcumulado >= 0 ? '+' : ''}${formatCLPFull(totalSaldoAcumulado)}`
                : undefined
            }
          >
            {`${totalSaldoAcumulado >= 0 ? '+' : ''}${formatCLPDisplay(totalSaldoAcumulado)}`}
          </span>
        }
        tone={totalSaldoAcumulado >= 0 ? 'primary' : 'danger'}
        size='secondary'
        valueClassName={`${kpiNumberClass} text-[clamp(1.8rem,5vw,2.4rem)]`}
      />
      <h2 className='text-xs sm:text-sm text-muted text-center'>
        Resultado anual
      </h2>
    </div>

    <div className='h-6' />

    <div className='flex flex-col items-center gap-1'>
      <KpiCard
        wrapper={false}
        align='center'
        value={
          <span className={kpiNumberClass} title={porcentajeLabel}>
            {porcentajeLabel}
          </span>
        }
        tone={porcentajeValor >= 0 ? 'primary' : 'danger'}
        size='secondary'
        valueClassName={`${kpiNumberClass} text-[clamp(1.6rem,4.5vw,2.2rem)]`}
      />
      <h2 className='text-xs sm:text-sm text-center text-muted'>
        Margen Operativo
      </h2>
    </div>
  </DashboardCard>
)
